/**
 * useReveal — shared IntersectionObservers for Tier 1 entrances
 * (Animation Spec §3.3 + §7).
 *
 *  Two observers, picked per element at observe-time:
 *
 *  - "Strict" (threshold 0, rootMargin "0px 0px -45% 0px") — root spans
 *    the top 55% of the viewport, so the fade fires when the element is
 *    roughly mid-screen instead of right above the fold. Tuned together
 *    with `--nyx-reveal-dur` (1100ms) and `--nyx-reveal-distance` (32px)
 *    in tokens.css. Threshold is 0 so IO emits a callback the moment any
 *    pixel of the element enters the root — see ensureObservers() for why
 *    a higher threshold loses elements on fast scrolls.
 *
 *  - "Loose" (threshold 0.05, rootMargin "0px") — used for elements near
 *    the bottom of the document that physically cannot scroll high enough
 *    to enter the strict root (footer, last section block on a tall page).
 *    Without this fallback, the page bottom would stay invisible forever.
 *    The loose observer trips as soon as the element is meaningfully on
 *    screen, which is the right behavior for end-of-page beats.
 *
 *  - Fires once per element. Once revealed, stays revealed —
 *    scrolling away never re-hides.
 *  - prefers-reduced-motion: skips observation entirely; reveals immediately.
 *
 * Why observation lives in `useEffect` (not the ref callback):
 *   React 18 StrictMode simulates an unmount+remount in dev. If we observed
 *   inside the ref callback and unobserved in `useEffect`'s cleanup, the
 *   second mount would never re-attach the ref (the DOM node is preserved),
 *   leaving the element permanently un-observed. Doing both observe + cleanup
 *   inside `useEffect` keeps them paired for every real or simulated cycle.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from './reducedMotion';

const REVEAL_CLASS = 'is-in';

/** Strict-observer rootMargin bottom shrink, expressed as a fraction of the
 * viewport height. Kept in sync with the rootMargin string below — used by
 * the strict-vs-loose decision so the threshold and the fallback policy
 * stay coupled. */
const STRICT_BOTTOM_SHRINK = 0.45;

/**
 * Extra slack added to the "can this element reach the strict root?" check.
 * Elements within this many pixels of the strict-reach boundary fall back
 * to the loose observer, because the IO's 20% threshold can fail to fire
 * reliably right at the edge of the maxScroll clamp. Empirically picked so
 * the bottom-most below-the-fold blocks (e.g. waitlist meta-rows on a
 * standard 1440×900 viewport) get the loose treatment. */
const STRICT_REACH_BUFFER_PX = 80;

let strictObserver: IntersectionObserver | null = null;
let looseObserver: IntersectionObserver | null = null;
const seen = new WeakSet<Element>();

function makeObserver(
  threshold: number,
  rootMargin: string,
): IntersectionObserver {
  return new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (seen.has(entry.target)) continue;
        // Reveal when the element is intersecting (the normal scroll-into-view path)
        // OR when it has already passed above the viewport (handles fast/instant
        // scrolls and #fragment landings — without this, an element the user
        // scrolled past in a single jump would stay opacity:0 forever).
        const passedAbove =
          !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        if (entry.isIntersecting || passedAbove) {
          seen.add(entry.target);
          entry.target.classList.add(REVEAL_CLASS);
          strictObserver?.unobserve(entry.target);
          looseObserver?.unobserve(entry.target);
        }
      }
    },
    { threshold, rootMargin },
  );
}

function ensureObservers(): {
  strict: IntersectionObserver | null;
  loose: IntersectionObserver | null;
} {
  if (typeof window === 'undefined') return { strict: null, loose: null };
  if (!strictObserver) {
    // Threshold MUST stay at 0. A higher threshold (e.g. 0.2) misses short
    // elements during fast scrolls / fling-scrolls: a 136px-tall card has
    // only a 27px "ratio ≥ 0.2" window inside the root, and if IO samples
    // before and after that window in a single frame, no threshold-crossing
    // callback fires and the element stays opacity:0 forever. With
    // threshold 0, any visibility transition emits a callback, which our
    // own intersecting/passedAbove logic handles cleanly.
    strictObserver = makeObserver(0, '0px 0px -45% 0px');
  }
  if (!looseObserver) {
    looseObserver = makeObserver(0.05, '0px');
  }
  return { strict: strictObserver, loose: looseObserver };
}

/**
 * True when the element sits low enough in the document that it can't be
 * scrolled into the strict root box (top 55% of viewport). Such elements
 * need the loose observer or they'd never reveal — the page simply runs
 * out of scroll before reaching their trigger position.
 */
function cannotReachStrictRoot(node: Element): boolean {
  const rect = node.getBoundingClientRect();
  const docTop = rect.top + window.scrollY;
  const docHeight = document.documentElement.scrollHeight;
  const vh = window.innerHeight;
  // Element top must be reachable at or below (1 - STRICT_BOTTOM_SHRINK) * vh
  // from the top of the viewport at the maximum possible scroll position.
  // The buffer keeps elements off the loose-vs-strict edge where IO's
  // threshold-crossing reliability degrades.
  const reachableMaxDocTop =
    docHeight - STRICT_BOTTOM_SHRINK * vh - STRICT_REACH_BUFFER_PX;
  return docTop > reachableMaxDocTop;
}

/**
 * Returns a ref callback to attach to the element you want to reveal.
 * Add the `nl-reveal` class to the same element (provided in `global.css`).
 */
export function useReveal<T extends HTMLElement>() {
  const elRef = useRef<T | null>(null);
  // Bumped by setRef whenever the underlying node changes, so the effect
  // below re-runs and re-observes the new node.
  const [nodeTick, setNodeTick] = useState(0);

  const setRef = useCallback((node: T | null) => {
    if (elRef.current === node) return;
    elRef.current = node;
    setNodeTick((t) => t + 1);
  }, []);

  useEffect(() => {
    const node = elRef.current;
    if (!node) return;

    if (prefersReducedMotion() || seen.has(node)) {
      node.classList.add(REVEAL_CLASS);
      return;
    }

    // Already above the fold on mount (e.g., #fragment landing, scroll
    // restoration, or Cmd+End from a previous session). The IO would never
    // fire a threshold-crossing event for these, so reveal immediately.
    const rect = node.getBoundingClientRect();
    if (rect.bottom <= 0) {
      seen.add(node);
      node.classList.add(REVEAL_CLASS);
      return;
    }

    const { strict, loose } = ensureObservers();
    const obs = cannotReachStrictRoot(node) ? loose : strict;
    if (!obs) return;
    obs.observe(node);
    return () => {
      obs.unobserve(node);
    };
  }, [nodeTick]);

  return setRef;
}
